import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSmartBack } from "@/lib/useSmartBack";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence, useAnimationControls } from "motion/react";
import { ArrowLeft, ArrowRight, Check, Compass, FileText, AlertTriangle } from "lucide-react";
import { friendlyError } from "@/lib/friendlyError";
import { useCompanyId } from "@/lib/CompanyContext";
import {
  escalaApi,
  type AlternativeQuestion,
  type EssayQuestion,
  type ResponseInput,
  type RoutingQuestion,
  type EscalaBusinessModel,
  type EscalaLetter,
} from "@/api/escala";
import { LoadingPlanet } from "@/components/LoadingPlanet";
import { Card, PageHeader, Notice, RequiredMark, EscalaWeightBadge } from "@/components/ui";

/**
 * EscalaWizard — formulário de qualificação v1.0 em 8 etapas:
 *   0 = P0 (modelo de negócio)
 *   1-6 = E, S, C, A, L, A2
 *   7 = Resumo / submit
 *
 * Salvamento automático em localStorage por companyId+versão. Submit
 * roda evaluator das 6 dissertativas via Haiku, leva ~30-60s. Loading
 * com LoadingPlanet enquanto isso.
 */

type Step =
  | { kind: "p0"; label: string }
  | { kind: "letter"; letter: EscalaLetter; label: string }
  | { kind: "summary"; label: string };

const STEPS: Step[] = [
  { kind: "p0", label: "Como você vende" },
  { kind: "letter", letter: "E", label: "Entender" },
  { kind: "letter", letter: "S", label: "Sistematizar" },
  { kind: "letter", letter: "C", label: "Captar" },
  { kind: "letter", letter: "A", label: "Ativar" },
  { kind: "letter", letter: "L", label: "Lucrar" },
  { kind: "letter", letter: "A2", label: "Acelerar" },
  { kind: "summary", label: "Revisar e enviar" },
];

const LETTER_DESCRIPTION: Record<EscalaLetter, string> = {
  E: "Quem é o cliente, qual a oferta, qual a dor",
  S: "Estrutura, processos, ferramentas, CRM",
  C: "Tráfego pago, conteúdo, geração de demanda",
  A: "Atendimento, qualificação, follow-up",
  L: "Faturamento, ticket, margem, ROI",
  A2: "Escala, expansão, parcerias",
};

// As 6 letras da metodologia, na ordem do wizard (A2 é o 2º "A" — Acelerar).
const LETTER_STEPS = STEPS.filter(
  (s): s is Extract<Step, { kind: "letter" }> => s.kind === "letter",
);

/** Régua visual E·S·C·A·L·A com a letra atual destacada. Faz o usuário PERCEBER
 *  que está percorrendo cada letra da metodologia (feedback: chegava ao fim sem
 *  se dar conta). `activeIdx`: índice da letra atual (0-5); = 6 no resumo (todas
 *  feitas). */
function EscalaLetterStrip({ activeIdx }: { activeIdx: number }) {
  return (
    <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
      {LETTER_STEPS.map((s, i) => {
        const display = s.letter === "A2" ? "A" : s.letter;
        const state = i < activeIdx ? "done" : i === activeIdx ? "current" : "upcoming";
        return (
          <span
            key={s.letter}
            title={s.label}
            style={{
              width: 24,
              height: 24,
              borderRadius: 999,
              flexShrink: 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11.5,
              fontWeight: 800,
              fontFamily: "var(--wf-font-display)",
              background:
                state === "current"
                  ? "var(--nd-orange)"
                  : state === "done"
                    ? "rgba(254,112,0,0.14)"
                    : "var(--wf-ink-5)",
              color:
                state === "current"
                  ? "#fff"
                  : state === "done"
                    ? "var(--nd-orange)"
                    : "var(--wf-ink-3)",
              transition: "background 150ms, color 150ms",
            }}
          >
            {display}
          </span>
        );
      })}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  background: "var(--wf-paper)",
  border: "1px solid var(--wf-ink-5)",
  borderRadius: 8,
  padding: "10px 12px",
  fontSize: 14,
  fontFamily: "var(--wf-font-body)",
  color: "var(--wf-ink)",
  width: "100%",
  outline: "none",
  resize: "vertical",
  minHeight: 100,
  lineHeight: 1.55,
};

// ─────────────────────────────────────────────────────────────
// Question UIs
// ─────────────────────────────────────────────────────────────

function RoutingQuestionUI({
  question,
  value,
  onChange,
}: {
  question: RoutingQuestion;
  value: string | undefined;
  onChange: (optionKey: string) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <h3
          className="wf-display"
          style={{ fontSize: 18, lineHeight: 1.25, marginTop: 0, marginBottom: 4 }}
        >
          {question.prompt}
          <RequiredMark />
        </h3>
        <p style={{ fontSize: 12, color: "var(--wf-ink-3)", margin: 0 }}>
          Escolha uma das opções abaixo de acordo com o seu modelo de negócios.
        </p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {question.options.map((opt) => {
          const selected = value === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => onChange(opt.key)}
              style={{
                cursor: "pointer",
                padding: "12px 14px",
                background: selected ? "#fff8ef" : "var(--wf-paper)",
                border: selected ? "1px solid var(--nd-orange)" : "1px solid var(--wf-ink-5)",
                borderRadius: 10,
                textAlign: "left",
                fontFamily: "var(--wf-font-body)",
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
              }}
            >
              <span
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 999,
                  border: selected ? "6px solid var(--nd-orange)" : "1px solid var(--wf-ink-4)",
                  background: selected ? "var(--nd-orange)" : "var(--wf-paper)",
                  flexShrink: 0,
                  marginTop: 2,
                }}
              />
              <span style={{ fontSize: 14, lineHeight: 1.5, color: "var(--wf-ink)" }}>
                {opt.text}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AlternativeQuestionUI({
  index,
  total,
  question,
  value,
  onChange,
}: {
  index: number;
  total: number;
  question: AlternativeQuestion;
  value: string | undefined;
  onChange: (optionKey: string) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div>
        <div
          style={{
            fontSize: 11,
            color: "var(--wf-ink-3)",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            marginBottom: 6,
          }}
        >
          Pergunta {index + 1} de {total}
          <EscalaWeightBadge weight={question.weight} />
        </div>
        <h3
          className="wf-display"
          style={{ fontSize: 17, lineHeight: 1.3, marginTop: 0, marginBottom: 0 }}
        >
          {question.prompt}
          <RequiredMark />
        </h3>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {question.options.map((opt) => {
          const selected = value === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => onChange(opt.key)}
              style={{
                cursor: "pointer",
                padding: "10px 12px",
                background: selected ? "#fff8ef" : "var(--wf-paper)",
                border: selected ? "1px solid var(--nd-orange)" : "1px solid var(--wf-ink-5)",
                borderRadius: 8,
                textAlign: "left",
                fontFamily: "var(--wf-font-body)",
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
              }}
            >
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 6,
                  background: selected ? "var(--nd-orange)" : "rgba(0,0,0,0.04)",
                  color: selected ? "#fff" : "var(--wf-ink-3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  fontSize: 12,
                  fontWeight: 700,
                  marginTop: 1,
                }}
              >
                {opt.key}
              </span>
              <span style={{ flex: 1, fontSize: 13, lineHeight: 1.5, color: "var(--wf-ink)" }}>
                {opt.text}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EssayQuestionUI({
  index,
  total,
  question,
  value,
  onChange,
}: {
  index: number;
  total: number;
  question: EssayQuestion;
  value: string | undefined;
  onChange: (text: string) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div>
        <div
          style={{
            fontSize: 11,
            color: "var(--wf-ink-3)",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            marginBottom: 6,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <FileText size={11} /> Pergunta {index + 1} de {total} · resposta livre
          <EscalaWeightBadge weight={question.weight} />
        </div>
        <h3
          className="wf-display"
          style={{ fontSize: 17, lineHeight: 1.3, marginTop: 0, marginBottom: 6 }}
        >
          {question.prompt}
        </h3>
        <p style={{ fontSize: 11, color: "var(--wf-ink-3)", margin: 0, lineHeight: 1.5 }}>
          Sua resposta será avaliada pela inteligência com base nos pontos mais relevantes da
          sua resposta. Quanto mais específico você melhor. Coloque números, exemplos, dores
          reais e as informações que achar pertinente à resposta.
        </p>
      </div>
      <textarea
        style={inputStyle}
        rows={5}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Escreva com suas palavras…"
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main wizard
// ─────────────────────────────────────────────────────────────

export default function EscalaWizard() {
  const navigate = useNavigate();
  const goBack = useSmartBack("/escala");
  const companyId = useCompanyId();
  const queryClient = useQueryClient();

  const [stepIdx, setStepIdx] = useState(0);
  // Âncora no topo do conteúdo — usada pra subir o scroll ao trocar de etapa.
  const topRef = useRef<HTMLDivElement>(null);
  const [businessModel, setBusinessModel] = useState<EscalaBusinessModel | null>(null);
  const [responses, setResponses] = useState<Record<string, ResponseInput>>({});
  // Mostra a mensagem do PORQUÊ não avançou quando a pessoa tenta sem responder
  // as obrigatórias (em vez de só desabilitar o botão sem explicação).
  const [showRequiredHint, setShowRequiredHint] = useState(false);
  // Shake do botão "Próximo" no clique bloqueado — feedback TÁTIL.
  const shakeControls = useAnimationControls();

  const draftKey = companyId ? `escala-wizard:${companyId}` : null;
  // `hydrated` evita o bug em que o efeito de PERSISTIR roda na montagem com o
  // estado inicial VAZIO e sobrescreve o draft salvo antes do hydrate rodar —
  // era o que fazia o usuário "perder" o que tinha preenchido ao Salvar e sair.
  const hydrated = useRef(false);

  // Hidrata do localStorage (respostas, modelo de negócio E o passo atual)
  useEffect(() => {
    if (!draftKey) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const saved = JSON.parse(raw) as {
          businessModel?: EscalaBusinessModel | null;
          responses?: Record<string, ResponseInput>;
          stepIdx?: number;
        };
        setBusinessModel(saved.businessModel ?? null);
        setResponses(saved.responses ?? {});
        if (typeof saved.stepIdx === "number") setStepIdx(saved.stepIdx);
      }
    } catch {
      /* ignore */
    }
    hydrated.current = true;
  }, [draftKey]);

  const saveDraft = useMemo(
    () => () => {
      if (!draftKey) return;
      localStorage.setItem(draftKey, JSON.stringify({ businessModel, responses, stepIdx }));
    },
    [draftKey, businessModel, responses, stepIdx],
  );

  // Persiste no localStorage — só DEPOIS de hidratar (senão sobrescreve com vazio).
  useEffect(() => {
    if (!draftKey || !hydrated.current) return;
    saveDraft();
  }, [draftKey, saveDraft]);

  // Busca questionário com routing aplicado
  const questionnaireQuery = useQuery({
    queryKey: ["escala-questionnaire", businessModel],
    queryFn: () => escalaApi.questionnaire(businessModel),
    staleTime: 5 * 60_000,
  });

  const allQuestions = questionnaireQuery.data?.questions ?? [];
  const p0 = allQuestions.find((q) => q.type === "routing") as RoutingQuestion | undefined;

  function questionsForLetter(
    letter: EscalaLetter,
  ): Array<AlternativeQuestion | EssayQuestion> {
    return allQuestions.filter(
      (q): q is AlternativeQuestion | EssayQuestion =>
        q.type !== "routing" && q.letter === letter,
    );
  }

  const setResponseFor = (questionKey: string, patch: Partial<ResponseInput>) => {
    setResponses((prev) => ({
      ...prev,
      [questionKey]: { ...prev[questionKey], ...patch, questionKey },
    }));
  };

  const submit = useMutation({
    mutationFn: async () => {
      if (!companyId) throw new Error("no company");
      const responseList = Object.values(responses);
      return escalaApi.submit(companyId, {
        businessModel,
        responses: responseList,
      });
    },
    onSuccess: () => {
      if (companyId) {
        queryClient.invalidateQueries({ queryKey: ["companies", companyId, "escala"] });
        localStorage.removeItem(`escala-wizard:${companyId}`);
      }
      navigate("/escala");
    },
  });

  // ─────────────────────────────────────────────────────────────
  // Validação por etapa
  // ─────────────────────────────────────────────────────────────

  const currentStep = STEPS[stepIdx]!;

  // Ao trocar de etapa (Próximo/Voltar), sobe o scroll pro topo. O wizard rola
  // dentro do <main> do Shell (não na janela), então subimos pelo ancestral
  // scrollável a partir da âncora; fallback pra window.
  useEffect(() => {
    let node: HTMLElement | null = topRef.current?.parentElement ?? null;
    while (node) {
      const oy = getComputedStyle(node).overflowY;
      if ((oy === "auto" || oy === "scroll") && node.scrollHeight > node.clientHeight) {
        node.scrollTo({ top: 0 });
        return;
      }
      node = node.parentElement;
    }
    window.scrollTo({ top: 0 });
  }, [stepIdx]);
  const canProceed = useMemo(() => {
    if (currentStep.kind === "p0") {
      return !!responses["p0"]?.optionKey;
    }
    if (currentStep.kind === "letter") {
      const qs = questionsForLetter(currentStep.letter);
      // Mínimo: todas as alternativas respondidas. Dissertativas opcionais.
      return qs
        .filter((q) => q.type === "alternative")
        .every((q) => !!responses[q.key]?.optionKey);
    }
    return true;
  }, [currentStep, responses, allQuestions]);

  // Esconde o aviso assim que a etapa fica válida ou a pessoa troca de passo.
  useEffect(() => {
    setShowRequiredHint(false);
  }, [stepIdx]);
  useEffect(() => {
    if (canProceed) setShowRequiredHint(false);
  }, [canProceed]);

  const totalSteps = STEPS.length;
  const progress = ((stepIdx + 1) / totalSteps) * 100;

  // Quando p0 muda, atualiza o businessModel
  useEffect(() => {
    const p0Resp = responses["p0"];
    if (p0Resp?.optionKey && p0) {
      const opt = p0.options.find((o) => o.key === p0Resp.optionKey);
      if (opt && opt.mapsTo !== businessModel) {
        setBusinessModel(opt.mapsTo);
      }
    }
  }, [responses, p0, businessModel]);

  if (!companyId) {
    return (
      <div style={{ padding: 32 }}>
        <span className="wf-hand-note">carregando empresa…</span>
      </div>
    );
  }

  if (questionnaireQuery.isLoading) {
    return (
      <div style={{ padding: 32 }}>
        <span className="wf-hand-note">carregando questionário…</span>
      </div>
    );
  }

  return (
    <div>
      <AnimatePresence>
        {submit.isPending && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(5,8,16,0.78)",
              backdropFilter: "blur(8px)",
              zIndex: 100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              padding: 24,
            }}
          >
            <LoadingPlanet
              size={140}
              label="Calculando seu diagnóstico ESCALA…"
              labelFontSize={18}
            />
            <p
              style={{
                marginTop: 22,
                color: "rgba(255,255,255,0.82)",
                fontSize: 16,
                maxWidth: 520,
                textAlign: "center",
                lineHeight: 1.55,
              }}
            >
              Avaliando suas respostas dissertativas, calculando os scores das 6 letras
              e gerando o perfil que vai pra Camada 2 do prompt dos seus agentes.
            </p>
            <div
              style={{
                marginTop: 20,
                padding: "13px 18px",
                background: "rgba(254,112,0,0.16)",
                border: "1px solid rgba(254,112,0,0.55)",
                borderRadius: 12,
                maxWidth: 520,
                textAlign: "center",
                fontSize: 15,
                lineHeight: 1.5,
                color: "#ffceb0",
              }}
            >
              Fique tranquilo, você pode fazer outras coisas no computador enquanto
              carregamos. Só não feche esta aba, por favor!
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div ref={topRef} aria-hidden />

      {/* Sem "Voltar" no topo: ele caía na tela de maturidade (sugerindo um novo
          diagnóstico) e confundia o fluxo, além de não salvar. A navegação é
          pelos botões do rodapé — "Voltar" (passo a passo) e "Salvar e sair"
          (que grava o rascunho). */}
      <PageHeader
        icon={Compass}
        kicker="ESCALA · Diagnóstico"
        title={currentStep.label}
        maxWidth={760}
      />

      <div style={{ padding: "16px 24px 0", maxWidth: 760, margin: "0 auto" }}>
        {/* Progress */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 22 }}>
          <div
            style={{
              height: 4,
              background: "var(--wf-ink-5)",
              borderRadius: 999,
              overflow: "hidden",
            }}
          >
            <motion.div
              style={{
                height: "100%",
                background: "var(--nd-orange)",
                borderRadius: 999,
              }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            />
          </div>
          {(currentStep.kind === "letter" || currentStep.kind === "summary") && (
            <div style={{ marginBottom: 4 }}>
              <EscalaLetterStrip
                activeIdx={
                  currentStep.kind === "summary"
                    ? LETTER_STEPS.length
                    : LETTER_STEPS.findIndex((s) => s.letter === currentStep.letter)
                }
              />
            </div>
          )}
          <div
            style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              fontSize: 11,
              color: "var(--wf-ink-3)",
            }}
          >
            <span>
              Etapa {stepIdx + 1} de {totalSteps}
            </span>
            {currentStep.kind === "letter" && (
              <>
                <span>·</span>
                <span>
                  Letra {LETTER_STEPS.findIndex((s) => s.letter === currentStep.letter) + 1} de{" "}
                  {LETTER_STEPS.length} · {LETTER_DESCRIPTION[currentStep.letter]}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          padding: "8px 24px 64px",
          maxWidth: 760,
          margin: "0 auto",
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={stepIdx}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            {currentStep.kind === "p0" && p0 && (
              <Card>
                <RoutingQuestionUI
                  question={p0}
                  value={responses["p0"]?.optionKey}
                  onChange={(opt) => setResponseFor("p0", { optionKey: opt })}
                />
              </Card>
            )}

            {currentStep.kind === "letter" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {questionsForLetter(currentStep.letter).map((q, i, arr) => (
                  <Card key={q.key}>
                    {q.type === "alternative" ? (
                      <AlternativeQuestionUI
                        index={i}
                        total={arr.length}
                        question={q}
                        value={responses[q.key]?.optionKey}
                        onChange={(opt) => setResponseFor(q.key, { optionKey: opt })}
                      />
                    ) : (
                      <EssayQuestionUI
                        index={i}
                        total={arr.length}
                        question={q}
                        value={responses[q.key]?.essayText}
                        onChange={(text) => setResponseFor(q.key, { essayText: text })}
                      />
                    )}
                  </Card>
                ))}
              </div>
            )}

            {currentStep.kind === "summary" && (
              <Card>
                <h3
                  className="wf-display"
                  style={{ fontSize: 20, marginTop: 0, marginBottom: 10, lineHeight: 1.25 }}
                >
                  Obrigado por completar o preenchimento. Temos tudo em mãos para
                  analisar com profundidade o seu negócio e fazer um diagnóstico completo.
                </h3>
                <p style={{ fontSize: 14, color: "var(--wf-ink-2)", lineHeight: 1.55 }}>
                  Vamos avaliar todas as suas respostas e calcular o score das 6 letras da
                  nossa metodologia E·S·C·A·L·A. Esse trabalho é para que toda ferramenta
                  fique contextualizada do seu negócio. Pode levar de 1 a 2 minutos para
                  finalizarmos.
                </p>
                <div
                  style={{
                    marginTop: 14,
                    padding: 12,
                    background: "rgba(255,112,0,0.06)",
                    border: "1px solid var(--nd-orange)",
                    borderRadius: 8,
                    fontSize: 12,
                    color: "var(--wf-ink-2)",
                    lineHeight: 1.55,
                  }}
                >
                  Lembrete: a nota máxima de cada letra da metodologia é 85%. Isso é
                  proposital, acreditamos que todo negócio tem espaço para evoluir.
                </div>
              </Card>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Nav */}
        <div
          style={{
            display: "flex",
            gap: 10,
            marginTop: 24,
            paddingTop: 16,
            borderTop: "1px solid var(--wf-ink-5)",
          }}
        >
          <button
            type="button"
            className="wf-btn wf-btn--ghost wf-btn--sm"
            onClick={() => (stepIdx === 0 ? goBack() : setStepIdx((i) => Math.max(0, i - 1)))}
            style={{ display: "flex", alignItems: "center", gap: 5 }}
          >
            <ArrowLeft size={12} strokeWidth={2} /> Voltar
          </button>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="wf-btn wf-btn--ghost wf-btn--sm"
            onClick={() => {
              saveDraft(); // grava o rascunho ANTES de sair (não perde o preenchido)
              goBack();
            }}
          >
            Salvar e sair
          </button>
          {currentStep.kind === "summary" ? (
            <button
              type="button"
              className="wf-btn wf-btn--primary"
              disabled={submit.isPending}
              onClick={() => submit.mutate()}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              {submit.isPending ? "Calculando…" : "Calcular diagnóstico"}
              <Check size={13} strokeWidth={2} color="#fff" />
            </button>
          ) : (
            <motion.button
              type="button"
              className="wf-btn wf-btn--primary"
              animate={shakeControls}
              aria-disabled={!canProceed}
              onClick={() => {
                if (!canProceed) {
                  // Bloqueado: explica + treme (visual + tátil), mas não avança.
                  setShowRequiredHint(true);
                  shakeControls.start({ x: [0, -8, 8, -6, 6, -3, 3, 0], transition: { duration: 0.42 } });
                  return;
                }
                setShowRequiredHint(false);
                setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                opacity: canProceed ? 1 : 0.55,
                cursor: canProceed ? "pointer" : "not-allowed",
              }}
            >
              Próximo <ArrowRight size={13} strokeWidth={2} color="#fff" />
            </motion.button>
          )}
        </div>

        {showRequiredHint && !canProceed && (
          <div style={{ marginTop: 12 }}>
            <Notice tone="danger">
              Responda todas as perguntas obrigatórias (marcadas com{" "}
              <span style={{ color: "#e5484d", fontWeight: 700 }}>*</span>) pra continuar.
            </Notice>
          </div>
        )}

      </div>

      {/* Pop-up de FALHA na avaliação da IA do ESCALA (em vez de erro inline). */}
      <AnimatePresence>
        {submit.isError && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => submit.reset()}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 100,
              background: "rgba(8,10,18,0.55)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 20,
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "100%",
                maxWidth: 420,
                background: "var(--wf-paper)",
                borderRadius: 14,
                padding: 24,
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 999,
                  background: "#fff0ee",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 14,
                }}
              >
                <AlertTriangle size={22} color="#e5484d" />
              </div>
              <h3 className="wf-display" style={{ fontSize: 19, margin: "0 0 6px" }}>
                Não consegui calcular seu diagnóstico
              </h3>
              <p style={{ fontSize: 13.5, lineHeight: 1.5, color: "var(--wf-ink-2)", margin: "0 0 18px" }}>
                {friendlyError((submit.error as Error)?.message)} Suas respostas continuam salvas — é
                só tentar de novo.
              </p>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button type="button" className="wf-btn wf-btn--ghost wf-btn--sm" onClick={() => submit.reset()}>
                  Fechar
                </button>
                <button
                  type="button"
                  className="wf-btn wf-btn--primary wf-btn--sm"
                  onClick={() => submit.mutate()}
                  disabled={submit.isPending}
                >
                  {submit.isPending ? "Tentando…" : "Tentar de novo"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
